import { useCallback, useEffect, useState } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { CarouselItem } from '@/components/ui/carousel';

interface SvgViewerProps {
  url: string;
  alt: string;
  className?: string;
}

export default function SvgViewer({
  url,
  alt,
  className = 'w-full h-full rounded-md object-contain',
}: SvgViewerProps) {
  const [svgUrl, setSvgUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const loadSvg = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch SVG: ${response.status}`);
      }

      const svgContent = await response.text();

      const blob = new Blob([svgContent], { type: 'image/svg+xml' });
      const blobUrl = URL.createObjectURL(blob);

      setSvgUrl(blobUrl);
      return blobUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load SVG');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [url]);

  useEffect(() => {
    let unmounted = false;
    let created = '';

    loadSvg().then((blobUrl) => {
      if (!blobUrl) return;
      created = blobUrl;
      // unmounted before the fetch landed, so cleanup already ran with ''
      if (unmounted) URL.revokeObjectURL(blobUrl);
    });

    return () => {
      unmounted = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [loadSvg]);

  if (isLoading) {
    return (
      <CarouselItem className="h-full">
        <Skeleton className="w-full h-full rounded-md" />
      </CarouselItem>
    );
  }

  if (error) {
    return (
      <CarouselItem className="h-full">
        <div className="w-full h-full rounded-md bg-gray-200 flex items-center justify-center">
          <p className="text-red-500">Failed to load SVG: {error}</p>
        </div>
      </CarouselItem>
    );
  }

  return (
    <CarouselItem className="h-full">
      <img src={svgUrl} alt={alt} className={className} />
    </CarouselItem>
  );
}
